/**
 * @file 讲义打印页 · 类型与展示数据
 * @description 打印申请行、筛选条件与本地模拟数据。接口未接入前由页面直接使用。
 *
 * @whenToUse 仅讲义打印页。
 * @whenNotToUse 其他业务模块。
 */

import {
  EducationStage,
  Grade,
  LearningLevel,
  Semester,
} from '@/enum/common.ts'

/**
 * 预览试卷模板。
 * knowledge：教案卷（KnowledgePrintTemplate）
 * stage：阶段卷（PrintTemplate）
 * knowledge-lite：精简教案卷（KnowledgePrintTemplateLite）
 * 空字符串：暂无试卷
 */
export type HandoutPaperKind = '' | 'knowledge' | 'stage' | 'knowledge-lite'

/** 打印状态 */
export type HandoutPrintStatus = 'pending' | 'printed'

/** 打印状态文案 */
export const HANDOUT_STATUS_LABEL: Record<HandoutPrintStatus, string> = {
  pending: '未打印',
  printed: '已打印',
}

/** 打印申请行（一名学员一条申请） */
export type HandoutPrintRow = {
  /** 申请 ID */
  id: string
  /** 学员名称 */
  studentName: string
  /** 教材名称 */
  textbookName: string
  /** 年级 */
  grade: Grade
  /** 学期 */
  semester: Semester
  /** 教育阶段 */
  educationStage: EducationStage
  /** 学习力等级 */
  learningGrade: LearningLevel
  /** 学习力值 */
  learningForce: number
  /** 所属教师名称 */
  teacherName: string
  /** 预览用试卷模板；空字符串表示暂无试卷 */
  paperKind: HandoutPaperKind
  /** 打印状态 */
  status: HandoutPrintStatus
}

/** 列表筛选条件 */
export type HandoutQuery = {
  /** 学员名称（包含匹配） */
  studentName: string
  /** 打印状态；空字符串表示全部 */
  status: string
}

export const EMPTY_HANDOUT_QUERY: HandoutQuery = {
  studentName: '',
  status: '',
}

/**
 * 按学员名称与打印状态过滤申请。
 * @param rows 全量申请
 * @param query 已提交的筛选条件
 * @returns 命中的申请
 */
export function filterHandoutRows(
  rows: HandoutPrintRow[],
  query: HandoutQuery,
): HandoutPrintRow[] {
  const name = query.studentName.trim()
  return rows.filter((row) => {
    const matchName = name === '' || row.studentName.includes(name)
    const matchStatus = query.status === '' || row.status === query.status
    return matchName && matchStatus
  })
}

/**
 * 当前校区、当前教师名下的打印申请。
 * 接入接口后删除，改由服务端按登录身份过滤。
 */
export const MOCK_HANDOUT_ROWS: HandoutPrintRow[] = [
  {
    id: 'h-1',
    studentName: '林晓晨',
    textbookName: '人教版数学七年级上册',
    grade: Grade.SEVEN,
    semester: Semester.FIRST,
    educationStage: EducationStage.JUNIOR_HIGH,
    learningGrade: LearningLevel.A,
    learningForce: 86,
    teacherName: '陈思远',
    paperKind: 'knowledge',
    status: 'pending',
  },
  {
    id: 'h-2',
    studentName: '周予安',
    textbookName: '北师大版数学八年级下册',
    grade: Grade.EIGHT,
    semester: Semester.SECOND,
    educationStage: EducationStage.JUNIOR_HIGH,
    learningGrade: LearningLevel.B,
    learningForce: 74,
    teacherName: '陈思远',
    paperKind: 'stage',
    status: 'pending',
  },
  {
    id: 'h-3',
    studentName: '苏晚宁',
    textbookName: '人教版物理九年级全一册',
    grade: Grade.NINE,
    semester: Semester.FIRST,
    educationStage: EducationStage.JUNIOR_HIGH,
    learningGrade: LearningLevel.A,
    learningForce: 91,
    teacherName: '陈思远',
    paperKind: 'knowledge-lite',
    status: 'printed',
  },
  {
    id: 'h-4',
    studentName: '许知夏',
    textbookName: '人教版数学五年级上册',
    grade: Grade.PRIMARY_FIVE,
    semester: Semester.FIRST,
    educationStage: EducationStage.PRIMARY_SCHOOL,
    learningGrade: LearningLevel.C,
    learningForce: 63,
    teacherName: '陈思远',
    paperKind: '',
    status: 'pending',
  },
  {
    id: 'h-5',
    studentName: '顾清和',
    textbookName: '人教版化学九年级上册',
    grade: Grade.NINE,
    semester: Semester.FIRST,
    educationStage: EducationStage.JUNIOR_HIGH,
    learningGrade: LearningLevel.B,
    learningForce: 78,
    teacherName: '陈思远',
    paperKind: '',
    status: 'printed',
  },
  {
    id: 'h-6',
    studentName: '沈星澜',
    textbookName: '人教A版数学必修一',
    grade: Grade.HIGH_ONE,
    semester: Semester.FIRST,
    educationStage: EducationStage.SENIOR_HIGH,
    learningGrade: LearningLevel.A,
    learningForce: 88,
    teacherName: '陈思远',
    paperKind: '',
    status: 'pending',
  },
  {
    id: 'h-7',
    studentName: '江澄',
    textbookName: '人教版数学三年级下册',
    grade: Grade.PRIMARY_THREE,
    semester: Semester.SECOND,
    educationStage: EducationStage.PRIMARY_SCHOOL,
    learningGrade: LearningLevel.D,
    learningForce: 52,
    teacherName: '陈思远',
    paperKind: '',
    status: 'printed',
  },
  {
    id: 'h-8',
    studentName: '叶晚舟',
    textbookName: '人教版物理八年级上册',
    grade: Grade.EIGHT,
    semester: Semester.FIRST,
    educationStage: EducationStage.JUNIOR_HIGH,
    learningGrade: LearningLevel.C,
    learningForce: 67,
    teacherName: '陈思远',
    paperKind: '',
    status: 'pending',
  },
  {
    id: 'h-9',
    studentName: '唐小满',
    textbookName: '人教版数学六年级上册',
    grade: Grade.PRIMARY_SIX,
    semester: Semester.FIRST,
    educationStage: EducationStage.PRIMARY_SCHOOL,
    learningGrade: LearningLevel.B,
    learningForce: 81,
    teacherName: '陈思远',
    paperKind: '',
    status: 'printed',
  },
  {
    id: 'h-10',
    studentName: '裴景行',
    textbookName: '人教A版数学必修二',
    grade: Grade.HIGH_TWO,
    semester: Semester.SECOND,
    educationStage: EducationStage.SENIOR_HIGH,
    learningGrade: LearningLevel.A,
    learningForce: 93,
    teacherName: '陈思远',
    paperKind: '',
    status: 'pending',
  },
]
